/**
 * ResearchRunsPanel — Agent runs view, sourced from the backend registry.
 *
 * Lists every deep run the user has launched (active + recent terminal),
 * driven by the backend `agent_run` field on session documents in Cosmos.
 * Each card shows goal, status, elapsed; clicking a card emits an open
 * callback so the parent can route back to that session.
 */
import { type ReactNode, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AgentRunModel } from "@/api/api"

import {
  CheckCircle2,
  Clock,
  Hourglass,
  Loader2,
  Square,
  Trash2,
  XCircle,
} from "@/components/icons"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import {
  formatAgentRunDateTime,
  formatAgentRunElapsed,
} from "@/features/ai-explorer/utils/agent-run-time"
import { cn } from "@/lib/utils"

import {
  isActiveAgentRunStatus,
  useCancelAgentRun,
  useDismissAgentRun,
  useMarkAgentRunRead,
  useVisibleAgentRuns,
} from "../../hooks/use-agent-runs-query"

interface ResearchRunsPanelProps {
  /** Invoked when the user clicks a card; parent routes to the session. */
  onOpen?: (run: AgentRunModel) => void
}

type AgentRunStatus = AgentRunModel["status"]
const RUN_FILTERS = ["all", "active", "done"] as const
type RunFilter = (typeof RUN_FILTERS)[number]

function statusIcon(status: AgentRunStatus): ReactNode {
  switch (status) {
    case "executing":
      return <Loader2 className="text-primary size-3.5 animate-spin motion-reduce:animate-none" />
    case "synthesizing":
      return <Hourglass className="text-muted-foreground size-3.5" />
    case "done":
      return <CheckCircle2 className="text-success size-3.5" />
    case "failed":
      return <XCircle className="text-destructive size-3.5" />
    case "cancelled":
      return <XCircle className="text-muted-foreground size-3.5" />
  }
  const exhaustive: never = status
  return exhaustive
}

function statusPillClass(status: AgentRunStatus): string {
  if (isActiveAgentRunStatus(status)) return "bg-primary/10 text-primary"
  if (status === "done") return STATUS_TONES.success.soft
  if (status === "failed") return "bg-destructive/10 text-destructive"
  return "bg-muted text-muted-foreground"
}

export function ResearchRunsPanel({ onOpen }: ResearchRunsPanelProps) {
  const { i18n, t } = useTranslation()
  const runs = useVisibleAgentRuns()
  const dismissMutation = useDismissAgentRun()
  const cancelMutation = useCancelAgentRun()
  const markReadMutation = useMarkAgentRunRead()
  const [filter, setFilter] = useState<RunFilter>("all")

  const visible = useMemo(() => {
    return runs.filter((r) => {
      if (filter === "all") return true
      if (filter === "active") return isActiveAgentRunStatus(r.status)
      return !isActiveAgentRunStatus(r.status)
    })
  }, [runs, filter])

  if (runs.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 px-6 py-8 text-center text-xs">
        <Clock className="size-6 opacity-50" />
        <p>
          {t("deepExplorer.runs_empty", {
            defaultValue:
              "No background agent runs yet. Approve a plan and click ‘Run in background’ to detach a Deep Explorer run.",
          })}
        </p>
      </div>
    )
  }

  return (
    <section
      aria-label={t("deepExplorer.runs_panel_aria", { defaultValue: "Agent runs" })}
      className="flex min-h-0 flex-1 flex-col"
    >
      <header className="border-border flex items-center gap-1 border-b px-3 py-1.5">
        {RUN_FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            className={cn(
              "rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors",
              filter === f
                ? "border-foreground bg-primary text-primary-foreground border"
                : "bg-muted text-muted-foreground hover:bg-muted/80 border border-transparent"
            )}
            onClick={() => setFilter(f)}
          >
            {t(`deepExplorer.runs_filter_${f}`, { defaultValue: f })}
          </button>
        ))}
      </header>
      <ul className="min-h-0 flex-1 overflow-y-auto p-3">
        {visible.map((run) => {
          const isActiveRun = isActiveAgentRunStatus(run.status)
          const startedLabel = formatAgentRunDateTime(run.started_at, i18n.language)
          const endedLabel = formatAgentRunDateTime(run.ended_at, i18n.language)
          const elapsedLabel = formatAgentRunElapsed(run.started_at, run.ended_at, {
            useCurrentTimeForMissingEnd: isActiveRun,
          })
          return (
            <li key={run.session_id} className="mb-2 last:mb-0">
              <div
                className={cn(
                  "border-border bg-card hover:border-primary/60 group rounded-md border px-3 py-2 transition-colors",
                  run.unread && !isActiveRun && "ring-primary/40 ring-1"
                )}
              >
                <div className="flex items-start gap-2">
                  <button
                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    type="button"
                    onClick={() => {
                      if (run.unread) markReadMutation.mutate(run.session_id)
                      onOpen?.(run)
                    }}
                  >
                    <span className="mt-0.5 shrink-0">{statusIcon(run.status)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="text-foreground block truncate text-sm font-medium">
                        {run.title ||
                          t("deepExplorer.runs_unnamed", { defaultValue: "Untitled run" })}
                      </span>
                      <span className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]">
                        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime data may omit status even if type says otherwise (see fix 569aeafc4) */}
                        {run.status ? (
                          <span
                            className={cn(
                              "rounded-full px-1.5 py-px font-medium tracking-wide uppercase",
                              statusPillClass(run.status)
                            )}
                          >
                            {t(`deepExplorer.runs_status_${run.status}`, {
                              defaultValue: run.status,
                            })}
                          </span>
                        ) : (
                          <span
                            className={cn(
                              "rounded-full px-1.5 py-px font-medium tracking-wide uppercase",
                              statusPillClass(run.status)
                            )}
                          >
                            {t("deepExplorer.runs_status_unknown", {
                              defaultValue: "—",
                            })}
                          </span>
                        )}
                        <span className="tabular-nums">
                          {t("deepExplorer.runs_duration", {
                            defaultValue: "{{elapsed}} runtime",
                            elapsed: elapsedLabel,
                          })}
                        </span>
                      </span>
                      <span className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] tabular-nums">
                        {startedLabel && run.started_at ? (
                          <span>
                            {t("deepExplorer.runs_started_prefix", {
                              defaultValue: "Started",
                            })}{" "}
                            <time dateTime={run.started_at}>{startedLabel}</time>
                          </span>
                        ) : null}
                        {endedLabel && run.ended_at ? (
                          <span>
                            {t("deepExplorer.runs_finished_prefix", {
                              defaultValue: "Finished",
                            })}{" "}
                            <time dateTime={run.ended_at}>{endedLabel}</time>
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </button>
                  {isActiveRun ? (
                    <button
                      className="text-muted-foreground hover:text-destructive shrink-0 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100"
                      type="button"
                      aria-label={t("deepExplorer.runs_cancel", {
                        defaultValue: "Cancel run",
                      })}
                      onClick={(e) => {
                        e.stopPropagation()
                        cancelMutation.mutate(run.session_id)
                      }}
                    >
                      <Square className="size-3.5" />
                    </button>
                  ) : (
                    <button
                      className="text-muted-foreground hover:text-destructive shrink-0 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100"
                      type="button"
                      aria-label={t("deepExplorer.runs_remove", {
                        defaultValue: "Dismiss run",
                      })}
                      onClick={(e) => {
                        e.stopPropagation()
                        dismissMutation.mutate(run.session_id)
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          )
        })}
        {visible.length === 0 && (
          <p className="text-muted-foreground py-4 text-center text-xs">
            {t("deepExplorer.runs_filter_empty", {
              defaultValue: "No runs match this filter.",
            })}
          </p>
        )}
      </ul>
    </section>
  )
}
